import {Action, createFeatureSelector, createReducer, createSelector, on} from '@ngrx/store';
import * as fromActions from './consent-actions';
import {AppStateNameEnum} from '@core/enums/app-state-name.enum';
import {addUnitsToOutlineState, createNewOutlineState, createNewOutlineStateWithSkippedUnits} from '../helpers/helper-outline';
import {UnitStatusEnum} from '@core/modules/rest/consent/enums/unit-status.enum';
import {CommentBasicModel} from '@core/modules/rest/consent/response/comment-basic.model';
import {OutlineChapterUnitBasicModel} from '@core/modules/rest/consent/response/outline-chapter-unit-basic.model';

export const REDUCER_NAME = AppStateNameEnum.feature_consent;

export interface State {
    consent: OutlineChapterUnitBasicModel[];
    currentUnitOrderId: number;
    consentDuration: number;
    comments: CommentBasicModel[];
    loading: boolean;
}

const initialState: State = {
    consent: [],
    currentUnitOrderId: 1,
    consentDuration: 0,
    comments: [],
    loading: false
};

const stateSelector = createFeatureSelector<State>(REDUCER_NAME);

export const consentSelector = createSelector(stateSelector, (state: State) => state.consent);
export const consentDurationSelector = createSelector(stateSelector, (state: State) => state.consentDuration);
export const commentSelector = createSelector(stateSelector, (state: State) => state.comments);
export const currentUnitOrderIdSelector = createSelector(stateSelector, (state: State) => state.currentUnitOrderId);
export const loadingSelector = createSelector(stateSelector, (state: State) => state.loading);

const innerReducer = createReducer(
    initialState,
    on(fromActions.GetConsent, ((state: State): State => {
        return ({
            ...state,
            loading: true
        });
    })),
    on(fromActions.GetConsentSuccess, ((state: State, {consent, currentUnitOrderId}): State => ({
        ...state,
        consent,
        currentUnitOrderId,
        loading: false
    }))),
    on(fromActions.GetConsentDuration, ((state: State): State => ({
        ...state,
        loading: false
    }))),
    on(fromActions.GetConsentDurationSuccess, ((state: State, {duration}): State => ({
        ...state,
        consentDuration: duration
    }))),
    on(fromActions.ResetState, ((): State => initialState)),
    on(fromActions.MarkUnitStatus, ((state: State, {unitOrderId, status, viewDuration}): State => {
        const newState: OutlineChapterUnitBasicModel[] = createNewOutlineState(state.consent, unitOrderId, [], status, viewDuration);
        return {
            ...state,
            consent: newState
        };
    })),
    on(fromActions.MarkSkippedUnitsStatuses, ((state: State, {unitsToMark, initialUnitOrderIdList, viewDuration}): State => {
        let newState: OutlineChapterUnitBasicModel[];
        const timeSkipped = new Date();
        newState = createNewOutlineStateWithSkippedUnits(state.consent, unitsToMark, initialUnitOrderIdList, viewDuration, timeSkipped);
        return {
            ...state,
            consent: newState
        };
    })),
    on(fromActions.LoadMoreUnits, ((state: State): State => {
        return {
            ...state
        };
    })),
    on(fromActions.LoadMoreUnitsSuccess, ((state: State, {unitOrderId, units, viewDuration}): State => {
        const markedState = createNewOutlineState(state.consent, unitOrderId, [], UnitStatusEnum.UNDERSTOOD, viewDuration);
        const newState = addUnitsToOutlineState(markedState, unitOrderId, units);
        return {
            ...state,
            consent: newState
        };
    })),
    on(fromActions.SubmitConsent, ((state): State => ({
        ...state,
    }))),
    on(fromActions.SubmitConsentSuccess, ((state): State => ({
        ...state,
    }))),
    on(fromActions.SubmitComment, ((state): State => ({
        ...state,
    }))),
    on(fromActions.SubmitCommentSuccess, ((state: State, {comment}): State => {
        return {
            ...state,
            comments: [...state.comments, comment]
        };
    })),
    on(fromActions.GetConsentComments, ((state: State): State => {
        return ({
            ...state
        });
    })),
    on(fromActions.GetConsentCommentsSuccess, ((state: State, {comments}): State => ({
        ...state,
        comments
    }))),
    on(fromActions.DeleteComment, ((state: State): State => ({
        ...state
    }))),
    on(fromActions.EditComment, ((state: State): State => ({
        ...state
    }))),
    on(fromActions.EditCommentSuccess, ((state: State, {response}): State => {
        const index = state.comments.findIndex(item => item.id === response.id);
        return {
            ...state,
            comments: [
                ...state.comments.slice(0, index),
                {
                    ...response
                },
                ...state.comments.slice(index + 1)
            ]
        };
    })),
    on(fromActions.AnswerComment, ((state): State => ({
        ...state,
    })))
);

export function consentReducer(state: State | undefined, action: Action): State {
    return innerReducer(state, action);
}

